/* UI-DESIGN: LB-70 the lobby as a dinner table (Taco picked place cards,
   docs/lightbox/lb69-lobby-concepts/b-placecards.jpg). Left: the party on
   glass with the host's moves. Right: one place card per guest, role
   pencilled on the fold, regrets turn the card down, open seats wait on the
   table. Every query, mutation and handler is unchanged:
   - Send roles / Start / Next round -> red primary (per game state)
   - Random assign, Host instructions -> secondary buttons
   - role select per guest            -> the pencilled line on the card
   - attendance checkbox              -> Coming / Not coming toggle
   - email_sent                       -> "Invite sent" on the card */
import { useEffect, type ReactNode } from "react";
import { useSelector } from "react-redux";
import clsx from "clsx";
import type { RootState } from "../../store/store";
import {
  useGetGameQuery,
  useAssignRoleMutation,
  useStartGameMutation,
  useUpdatePlayerMutation,
  useRandomAssignMutation,
  useAdvanceMutation,
} from "../../reducers/apiSlice";
import { Navigate, useSearchParams } from "react-router-dom";
import { useToast } from "../../components/ui/toast";
import convertErrorMessage from "../../utils/convertErrorMessage";
import { GameState, type Game } from "../../types/game";
import { SAMPLE_LOBBIES } from "./sampleLobby";
/* UI-DESIGN: real icons for the host actions */
import { BookOpen, Shuffle } from "@gravity-ui/icons";
/* UI-DESIGN: CaseMark wraps the puzzle set's mark (Lotus today, swappable per theme) */
import CaseMark from "../../components/icons/CaseMark";
import LightboxSurface from "../../components/lightbox/LightboxSurface";
import GlassPanel from "../../components/lightbox/GlassPanel";
import { frameFor } from "../../components/lightbox/frames";
import { initialTypeface } from "../../components/lightbox/typefaces";
import { loadHand } from "../../components/lightbox/handFonts";
import styles from "./lobby.module.scss";

const noop = () => undefined;
/* UI-DESIGN: LB-54 every lobby state sits in the same lit room */
const Room = ({ children }: { children: ReactNode }) => (
  <LightboxSurface mode="static" typeface={initialTypeface()} onUnavailable={noop}>
    {children}
  </LightboxSurface>
);

const HOST_INSTRUCTIONS_URL =
  "https://puzzlestorage221.blob.core.windows.net/publictesting/Host%20Instructions.pdf";

const getDisplayText = (game: Game | null) => {
  const gameState = game?.game_state;
  const currentRound = game?.current_round;
  switch (gameState) {
    case GameState.INVITING:
      return "Inviting";
    case GameState.PRE_GAME:
      return "Ready to start";
    case GameState.IN_PROGRESS:
      return `In progress, round ${currentRound}`;
    default:
      return "Unknown";
  }
};

const SampleLobby = () => {
  /* UI-DESIGN: the pencil on the place cards is the case files' hand */
  useEffect(() => {
    void loadHand();
  }, []);
  const [searchParams] = useSearchParams();
  const gameId = searchParams.get("gameId");
  /* UI-DESIGN: sample ids skip the API so the lobby can be reviewed logged out */
  const sample = gameId ? SAMPLE_LOBBIES[gameId] ?? null : null;
  const { isError, isLoading, error } = useGetGameQuery(gameId as string, { skip: !gameId || !!sample });
  const liveGame = useSelector((state: RootState) => state.game.game);
  const game = sample ?? liveGame;
  const players = game?.players;
  const [updatePlayerRole] = useAssignRoleMutation();
  const [startGame, { isLoading: isStartingGame }] = useStartGameMutation();
  const { triggerToast } = useToast();
  const [updatePlayer, { isLoading: isUpdatingPlayer }] = useUpdatePlayerMutation();
  const [randomAssign] = useRandomAssignMutation();
  const [advanceGame, { isLoading: isAdvancingGame }] = useAdvanceMutation();

  const handleStartGame = () => {
    startGame({ game_id: gameId as string }).unwrap().then(() => {
      triggerToast({
        title: "Game Started",
        description: "Emails have been sent to all players",
      });
    }).catch((err) => {
      triggerToast({
        title: "Error",
        description: convertErrorMessage(err),
      });
    });
  };

  const handleRandomAssign = () => {
    randomAssign({ game: gameId as string }).unwrap().then(() => {
      triggerToast({
        title: "Random Assign",
        description: "Random assignment has been completed",
      });
    }).catch((err) => {
      triggerToast({
        title: "Error",
        description: convertErrorMessage(err),
      });
    });
  };

  const handleAdvanceGame = () => {
    advanceGame({ game_id: gameId as string }).unwrap().then(() => {
      if (game?.game_state === GameState.IN_PROGRESS) {
        triggerToast({
          title: "Game Advanced",
          description: "Game has been advanced",
        });
      } else {
        triggerToast({
          title: "Game Started",
          description: "Game has been started",
        });
      }
    });
  };

  if (isError) {
    return (
      <Room>
        <div className={styles.statusScreen}>
          <GlassPanel className={styles.statusPanel}>
            {/* @ts-expect-error error shape comes from the backend */}
            <p>Error: {error?.data?.detail}</p>
          </GlassPanel>
        </div>
      </Room>
    );
  }

  if (isLoading) {
    /* UI-DESIGN: skeleton mirrors the two-column layout so the real page
       doesn't jump when data arrives */
    return (
      <Room>
      <div className={styles.page} aria-busy="true" aria-label="Loading lobby">
        <section className={styles.details}>
          <span className={clsx("ui-shimmer", styles.skeletonTitle)} />
          <span className={clsx("ui-shimmer", styles.skeletonChip)} />
          <span className={clsx("ui-shimmer", styles.skeletonBlock)} />
        </section>
        <section>
          <span className={clsx("ui-shimmer", styles.skeletonTitle)} />
          {[0, 1, 2, 3].map((i) => (
            <span key={i} className={clsx("ui-shimmer", styles.skeletonRow)} />
          ))}
        </section>
      </div>
      </Room>
    );
  }

  const attendingCount = players?.filter((player) => player.attendance).length ?? 0;
  const caseFrame = frameFor(game?.mystery?.name);
  /* UI-DESIGN: LB-70 one place per seat the case plays; the ones nobody has
     taken yet stay on the table as open seats */
  const seats = Math.max(game?.mystery?.number_of_players ?? 0, players?.length ?? 0);
  const openSeats = Array.from({ length: seats - (players?.length ?? 0) }, (_, i) => (players?.length ?? 0) + i + 1);

  return (
    <Room>
    <div className={styles.page}>
      {/* —— Left: the party and the host's moves —— */}
      <GlassPanel as="section" className={styles.details} aria-labelledby="event-title">
        <p className={styles.detailsLabel}>
          {getDisplayText(game)}
          {game?.mystery?.name ? `: ${game.mystery.name}` : null}
        </p>
        <h1 id="event-title" className={styles.title}>
          {game?.name ?? "Murder Mystery"}
        </h1>

        {/* UI-DESIGN: LB-54 the case's own file; the mark stays for cases without one */}
        {caseFrame ? (
          <img className={styles.caseFile} src={caseFrame} alt="" decoding="async" />
        ) : (
          <CaseMark className={styles.lotus} />
        )}

        {game?.mystery?.description ? (
          <p className={styles.description}>{game.mystery.description}</p>
        ) : null}

        <div className={styles.actions}>
          {/* UI-DESIGN: the one move that takes the night forward, per state; same handlers */}
          {game?.game_state === GameState.INVITING && (
            <button type="button" className={styles.primaryAction} onClick={handleStartGame} disabled={!!sample || isStartingGame}>
              {isStartingGame ? "Sending roles…" : "Send roles out"}
            </button>
          )}
          {game?.game_state === GameState.PRE_GAME && (
            <button type="button" className={styles.primaryAction} onClick={handleAdvanceGame} disabled={!!sample || isAdvancingGame}>
              {isAdvancingGame ? "Starting…" : "Start round one"}
            </button>
          )}
          {game?.game_state === GameState.IN_PROGRESS && (
            <button type="button" className={styles.primaryAction} onClick={handleAdvanceGame} disabled={!!sample || isAdvancingGame}>
              {isAdvancingGame ? "Moving on…" : "Next round"}
            </button>
          )}
          {game?.game_state === GameState.INVITING && (
            <button type="button" className={styles.secondaryAction} onClick={handleRandomAssign} disabled={!!sample}>
              <Shuffle width={16} height={16} aria-hidden />
              Random roles for the rest
            </button>
          )}
          <button type="button" className={styles.secondaryAction} onClick={() => window.open(HOST_INSTRUCTIONS_URL, "_blank")}>
            <BookOpen width={16} height={16} aria-hidden />
            Host instructions
          </button>
        </div>
      </GlassPanel>

      {/* —— Right: the table. LB-70 (Taco picked place cards over the roster
          sheet and passports): one card per guest, their role in pencil,
          regrets fold the card down, empty seats wait on the table —— */}
      <section className={styles.table} aria-labelledby="table-title">
        <header className={styles.tableHeader}>
          <h2 id="table-title" className={styles.tableTitle}>The table</h2>
          <p className={styles.count} aria-live="polite">
            {attendingCount} of {seats || "—"} coming
          </p>
        </header>

        <ul className={styles.cards}>
          {players?.map((player) => (
            <li key={player.objectId} className={clsx(styles.card, !player.attendance && styles.cardDown)}>
              <span className={styles.sent}>{player.email_sent ? "Invite sent" : "Not sent yet"}</span>
              <span className={styles.name}>{player.name}</span>
              <span className={styles.contact}>{player.email || player.phone_number}</span>

              <div className={styles.fold}>
                {player.attendance ? (
                  /* UI-DESIGN: same role mutation; the select reads as a pencilled line */
                  <select
                    className={clsx(styles.role, !player.role?.objectId && styles.roleEmpty)}
                    aria-label={`Role for ${player.name}`}
                    disabled={!!sample}
                    value={player.role?.objectId ?? ""}
                    onChange={(e) => {
                      updatePlayerRole({
                        player: player.objectId,
                        target_player: player.objectId,
                        target_role: e.target.value,
                      });
                    }}
                  >
                    <option value="">pick a role</option>
                    {game?.mystery?.roles.map((role) => (
                      <option key={role.objectId} value={role.objectId}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className={styles.regrets}>sends regrets</span>
                )}
                {/* UI-DESIGN: attendance toggle, same mutation */}
                <button
                  type="button"
                  className={styles.rsvp}
                  onClick={() => updatePlayer({ objectId: player.objectId, attendance: !player.attendance })}
                  disabled={!!sample || isUpdatingPlayer}
                  aria-pressed={player.attendance}
                  aria-label={`${player.name} is ${player.attendance ? "coming" : "not coming"}. Change`}
                >
                  {player.attendance ? "Coming" : "Not coming"}
                </button>
              </div>
            </li>
          ))}
          {openSeats.map((n) => (
            <li key={`seat-${n}`} className={styles.openSeat}>
              Seat {n}, open
            </li>
          ))}
        </ul>

        {players && players.length === 0 ? (
          <p className={styles.empty}>
            Nobody's at the table yet. Copy your invite link and send it round.
          </p>
        ) : null}
      </section>
    </div>
    </Room>
  );
};

/* UI-DESIGN: LB-81 real lobbies use Avi's host-aware flow, not the old
   prototype's unrestricted controls. Keep sample IDs available for design review. */
const Lobby = () => {
  const [params] = useSearchParams();
  const gameId = params.get("gameId");
  if (gameId && SAMPLE_LOBBIES[gameId]) return <SampleLobby />;
  return <Navigate to={gameId ? `/game/${encodeURIComponent(gameId)}` : "/open-cases"} replace />;
};

export default Lobby;
